import { useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type ColumnDef,
  type FilterFn,
  type SortingState,
  type Table as TanStackTable,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { differenceInCalendarDays } from "date-fns";
import { ArrowDownUp, Search, X } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Spinner } from "@/components/ui/spinner";
import { WFPButton } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SortableColumnHeader, getColumnSortAria } from "@/components/ui/sortable-column-header";
import { cn } from "@/lib/utils";
import { formatDateTimeCompact } from "../../utils/relative-time";
import { TRADER_TYPE_LABEL_KEYS } from "../TraderDetails/components/Form/Components/TraderDetailsStepOne/utils";
import { type TraderRepresentativeItem } from "../TraderDetailsModal/components/RepresentativesTable";

interface RepresentativeColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type RepresentativeColumnDef = ColumnDef<TraderRepresentativeItem, unknown> & {
  meta?: RepresentativeColumnMeta;
};

const EMPTY_VALUE = "-";

const SORTING_MENU_ITEM_CLASS =
  "data-[state=checked]:bg-primary-50 data-[state=checked]:font-medium data-[state=checked]:text-primary-700 focus:bg-primary-100 focus:text-primary-700";

const COLUMN_LABEL_PREFIX = "home.manager.overview.buyerRepresentativesModal.columns";

const REPRESENTATIVE_SORT_OPTIONS = [
  {
    id: "organization",
    translationKey: `${COLUMN_LABEL_PREFIX}.organization`,
  },
  {
    id: "type",
    translationKey: `${COLUMN_LABEL_PREFIX}.type`,
  },
  {
    id: "location",
    translationKey: `${COLUMN_LABEL_PREFIX}.location`,
  },
  {
    id: "name",
    translationKey: "column.name",
  },
  {
    id: "email",
    translationKey: "column.email",
  },
  {
    id: "firstLogin",
    translationKey: `${COLUMN_LABEL_PREFIX}.firstLogin`,
  },
  {
    id: "lastLogin",
    translationKey: `${COLUMN_LABEL_PREFIX}.lastLogin`,
  },
] as const;

type RepresentativeStatus = "active" | "inactive" | "never";

const ACTIVE_LOGIN_WINDOW_DAYS = 30;

const STATUS_CONFIG: Record<
  RepresentativeStatus,
  { translationKey: string; textClassName: string; dotClassName: string }
> = {
  active: {
    translationKey: "home.manager.overview.buyerRepresentativesModal.status.active",
    textClassName: "text-green-600",
    dotClassName: "bg-green-600",
  },
  inactive: {
    translationKey: "home.manager.overview.buyerRepresentativesModal.status.inactive",
    textClassName: "text-ivory-600",
    dotClassName: "bg-ivory-600",
  },
  never: {
    translationKey: "home.manager.overview.buyerRepresentativesModal.status.neverActive",
    textClassName: "text-red-600",
    dotClassName: "bg-red-600",
  },
};

const getColumnMeta = (columnDef: ColumnDef<TraderRepresentativeItem, unknown>) =>
  columnDef.meta as RepresentativeColumnMeta | undefined;

const getRepresentativeFullName = (representative: TraderRepresentativeItem) => {
  const person = representative.person;
  const fullName = person?.full_name?.trim();

  if (fullName) {
    return fullName;
  }

  return [person?.first_name, person?.last_name]
    .filter((name): name is string => Boolean(name?.trim()))
    .join(" ")
    .trim();
};

const getRepresentativePhone = (representative: TraderRepresentativeItem) =>
  representative.person?.contact_phone?.trim() || "";

const getRepresentativeEmail = (representative: TraderRepresentativeItem) =>
  representative.person?.contact_email?.trim() || "";

const getRepresentativeFirstLoginDate = (representative: TraderRepresentativeItem) =>
  representative.first_login ?? "";

const getRepresentativeFirstLogin = (representative: TraderRepresentativeItem) =>
  formatDateTimeCompact(getRepresentativeFirstLoginDate(representative));

const getRepresentativeOrgId = (representative: TraderRepresentativeItem) => {
  const id = representative.organization?.id;
  return id !== undefined && id !== null ? String(id) : "";
};

const getRepresentativeOrgName = (representative: TraderRepresentativeItem) =>
  representative.organization?.name?.trim() || "";

const getRepresentativeTypeLabel = (representative: TraderRepresentativeItem, t: TFunction) => {
  const traderType = representative.organization?.trader_type?.trim();

  if (!traderType) {
    return "";
  }

  const labelKey = TRADER_TYPE_LABEL_KEYS[traderType];
  return labelKey ? t(labelKey) : traderType;
};

const getRepresentativeLocationArea = (representative: TraderRepresentativeItem) =>
  representative.organization?.location_details?.name?.trim() || "";

const getRepresentativeAddress = (representative: TraderRepresentativeItem) =>
  representative.organization?.physical_address?.trim() || "";

const getRepresentativeLastLoginDate = (representative: TraderRepresentativeItem) =>
  representative.last_login ?? "";

const getRepresentativeLastLogin = (representative: TraderRepresentativeItem) =>
  formatDateTimeCompact(getRepresentativeLastLoginDate(representative));

export const getRepresentativeStatus = (
  representative: TraderRepresentativeItem
): RepresentativeStatus => {
  const lastLogin = getRepresentativeLastLoginDate(representative);

  if (!lastLogin) {
    return "never";
  }

  const lastLoginDate = new Date(lastLogin);

  if (Number.isNaN(lastLoginDate.getTime())) {
    return "never";
  }

  return differenceInCalendarDays(new Date(), lastLoginDate) <= ACTIVE_LOGIN_WINDOW_DAYS
    ? "active"
    : "inactive";
};

const formatValue = (value: string) => value || EMPTY_VALUE;

const RepresentativeStatusBadge = ({ status }: { status: RepresentativeStatus }) => {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[status];

  return (
    <span
      className={cn("inline-flex items-center gap-1.5 text-xs font-medium", config.textClassName)}
    >
      <span className={cn("size-1.5 rounded-full", config.dotClassName)} aria-hidden="true" />
      {t(config.translationKey)}
    </span>
  );
};

const getRepresentativeRowId = (representative: TraderRepresentativeItem, index: number) => {
  if (representative.id !== undefined && representative.id !== null) {
    return String(representative.id);
  }

  return (
    getRepresentativeEmail(representative) ||
    getRepresentativePhone(representative) ||
    `${getRepresentativeFullName(representative)}-${index}`
  );
};

const searchFilter: FilterFn<TraderRepresentativeItem> = (row, _columnId, filterValue) => {
  const term = String(filterValue).trim().toLowerCase();

  if (!term) {
    return true;
  }

  const representative = row.original;

  return [
    getRepresentativeOrgName(representative),
    getRepresentativeLocationArea(representative),
    getRepresentativeAddress(representative),
    getRepresentativeFullName(representative),
    getRepresentativeEmail(representative),
    getRepresentativePhone(representative),
  ].some(field => field.toLowerCase().includes(term));
};

const createColumns = (t: TFunction): RepresentativeColumnDef[] => [
  {
    id: "organization",
    accessorFn: getRepresentativeOrgName,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t(`${COLUMN_LABEL_PREFIX}.organization`)} />
    ),
    cell: ({ row }) => {
      const orgName = getRepresentativeOrgName(row.original);
      const orgId = getRepresentativeOrgId(row.original);

      if (!orgName) {
        return EMPTY_VALUE;
      }

      if (!orgId) {
        return <span className="font-medium text-foreground">{orgName}</span>;
      }

      return (
        <Link
          to={`/wfpManager/traders?view=${orgId}`}
          className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          {orgName}
        </Link>
      );
    },
    meta: {
      cellClassName: "min-w-48 whitespace-normal leading-5",
      headerClassName: "min-w-48",
    },
  },
  {
    id: "type",
    accessorFn: representative => getRepresentativeTypeLabel(representative, t),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t(`${COLUMN_LABEL_PREFIX}.type`)} />
    ),
    cell: ({ row }) => formatValue(getRepresentativeTypeLabel(row.original, t)),
    meta: {
      cellClassName: "min-w-36",
      headerClassName: "min-w-36",
    },
  },
  {
    id: "location",
    accessorFn: getRepresentativeLocationArea,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t(`${COLUMN_LABEL_PREFIX}.location`)} />
    ),
    cell: ({ row }) => {
      const area = getRepresentativeLocationArea(row.original);
      const address = getRepresentativeAddress(row.original);

      if (!area && !address) {
        return EMPTY_VALUE;
      }

      return (
        <div className="flex flex-col leading-5">
          {area ? <span className="text-foreground">{area}</span> : null}
          {address ? <span className="text-muted-foreground text-xs">{address}</span> : null}
        </div>
      );
    },
    meta: {
      cellClassName: "min-w-52 whitespace-normal",
      headerClassName: "min-w-52",
    },
  },
  {
    id: "name",
    accessorFn: getRepresentativeFullName,
    header: ({ column }) => <SortableColumnHeader column={column} label={t("column.name")} />,
    cell: ({ row }) => (
      <span className="font-medium text-foreground">
        {formatValue(getRepresentativeFullName(row.original))}
      </span>
    ),
    meta: {
      cellClassName: "min-w-48 whitespace-normal leading-5",
      headerClassName: "min-w-48",
    },
  },
  {
    id: "email",
    accessorFn: getRepresentativeEmail,
    header: ({ column }) => <SortableColumnHeader column={column} label={t("column.email")} />,
    cell: ({ row }) => formatValue(getRepresentativeEmail(row.original)),
    meta: {
      cellClassName: "min-w-64 whitespace-normal break-words leading-5 text-muted-foreground",
      headerClassName: "min-w-64",
    },
  },
  {
    id: "mobile",
    accessorFn: getRepresentativePhone,
    header: () => t("column.mobile"),
    enableSorting: false,
    cell: ({ row }) => formatValue(getRepresentativePhone(row.original)),
    meta: {
      cellClassName: "min-w-36",
      headerClassName: "min-w-36",
    },
  },
  {
    id: "firstLogin",
    accessorFn: getRepresentativeFirstLoginDate,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t(`${COLUMN_LABEL_PREFIX}.firstLogin`)} />
    ),
    cell: ({ row }) => formatValue(getRepresentativeFirstLogin(row.original)),
    meta: {
      cellClassName: "w-44 text-muted-foreground",
      headerClassName: "w-44",
    },
  },
  {
    id: "lastLogin",
    accessorFn: getRepresentativeLastLoginDate,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t(`${COLUMN_LABEL_PREFIX}.lastLogin`)} />
    ),
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground">
          {formatValue(getRepresentativeLastLogin(row.original))}
        </span>
        <RepresentativeStatusBadge status={getRepresentativeStatus(row.original)} />
      </div>
    ),
    meta: {
      cellClassName: "w-48",
      headerClassName: "w-48",
    },
  },
];

const RepresentativesDataTable = ({
  emptyMessage,
  isLoading,
  table,
}: {
  emptyMessage: string;
  isLoading: boolean;
  table: TanStackTable<TraderRepresentativeItem>;
}) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                className={getColumnMeta(header.column.columnDef)?.headerClassName}
                aria-sort={getColumnSortAria(header.column)}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-20">
              <div className="flex items-center justify-center">
                <Spinner className="size-5 text-muted-foreground" />
              </div>
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length
          ? rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
  );
};

interface RepresentativesToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sorting: SortingState;
  onSortColumnChange: (columnId: string) => void;
  onSortDirectionChange: (direction: string) => void;
}

const RepresentativesToolbar = ({
  searchTerm,
  onSearchChange,
  sorting,
  onSortColumnChange,
  onSortDirectionChange,
}: RepresentativesToolbarProps) => {
  const { t } = useTranslation();
  const selectedSort = sorting[0];
  const selectedSortOption = REPRESENTATIVE_SORT_OPTIONS.find(
    sortOption => sortOption.id === selectedSort?.id
  );
  const selectedSortColumn = selectedSortOption?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";
  const sortButtonLabel = selectedSortOption
    ? t(selectedSortOption.translationKey)
    : t("home.manager.overview.buyerRepresentativesModal.sort.button");

  return (
    <div className="flex flex-wrap items-center gap-2">
      <InputGroup className="w-full sm:w-[28rem]">
        <InputGroupAddon>
          <Search className="size-4" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          value={searchTerm}
          className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          placeholder={t("home.manager.overview.buyerRepresentativesModal.searchPlaceholder")}
          aria-label={t("home.manager.overview.buyerRepresentativesModal.searchPlaceholder")}
          onChange={event => onSearchChange(event.target.value)}
        />
        {searchTerm ? (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={t("home.manager.overview.buyerRepresentativesModal.clearSearch")}
              onClick={() => onSearchChange("")}
            >
              <X className="size-3.5" aria-hidden="true" />
            </InputGroupButton>
          </InputGroupAddon>
        ) : null}
      </InputGroup>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <WFPButton type="button" variant="outline">
            <ArrowDownUp className="size-4" aria-hidden="true" />
            {sortButtonLabel}
          </WFPButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>
            {t("home.manager.overview.buyerRepresentativesModal.sort.sortBy")}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup value={selectedSortColumn} onValueChange={onSortColumnChange}>
            {REPRESENTATIVE_SORT_OPTIONS.map(sortOption => (
              <DropdownMenuRadioItem
                key={sortOption.id}
                value={sortOption.id}
                indicator="check"
                className={SORTING_MENU_ITEM_CLASS}
              >
                {t(sortOption.translationKey)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>
            {t("home.manager.overview.buyerRepresentativesModal.sort.order")}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={selectedSortDirection}
            onValueChange={onSortDirectionChange}
          >
            <DropdownMenuRadioItem
              value="asc"
              indicator="check"
              className={SORTING_MENU_ITEM_CLASS}
            >
              {t("home.manager.overview.buyerRepresentativesModal.sort.ascending")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem
              value="desc"
              indicator="check"
              className={SORTING_MENU_ITEM_CLASS}
            >
              {t("home.manager.overview.buyerRepresentativesModal.sort.descending")}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

interface RepresentativesTableProps {
  isFetchingRepresentatives?: boolean;
  representatives?: TraderRepresentativeItem[];
}

export function RepresentativesTable({
  isFetchingRepresentatives = false,
  representatives = [],
}: RepresentativesTableProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo(() => createColumns(t), [t]);

  const table = useReactTable({
    columns,
    data: representatives,
    enableMultiSort: false,
    enableSortingRemoval: false,
    globalFilterFn: searchFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: getRepresentativeRowId,
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setSearchTerm,
    onSortingChange: setSorting,
    state: {
      globalFilter: searchTerm,
      sorting,
    },
  });

  const onSortColumnChange = (columnId: string) => {
    setSorting([{ id: columnId, desc: sorting[0]?.desc ?? false }]);
  };

  const onSortDirectionChange = (direction: string) => {
    setSorting([
      { id: sorting[0]?.id ?? REPRESENTATIVE_SORT_OPTIONS[0].id, desc: direction === "desc" },
    ]);
  };

  return (
    <div className="flex flex-col gap-4">
      <RepresentativesToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sorting={sorting}
        onSortColumnChange={onSortColumnChange}
        onSortDirectionChange={onSortDirectionChange}
      />
      <div className="overflow-x-auto rounded-md border border-border bg-background">
        <RepresentativesDataTable
          emptyMessage={t("home.manager.overview.buyerRepresentativesModal.empty")}
          isLoading={isFetchingRepresentatives}
          table={table}
        />
      </div>
    </div>
  );
}
